import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Users, Trash2, X, RefreshCw, Search, Shield, Mail, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../utils/api';
import { format } from 'date-fns';

const AdminUsers = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [addingToEmailList, setAddingToEmailList] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone and will delete all their bookings.`)) {
      return;
    }

    setDeletingUserId(userId);
    try {
      await apiClient.delete(`/admin/users/${userId}`);
      toast.success(`User "${userName}" deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleAddToEmailList = async (userId, userName) => {
    setAddingToEmailList(userId);
    try {
      await apiClient.post(`/admin/users/${userId}/add-to-email-list`);
      toast.success(`${userName} added to saved email list`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to email list');
    } finally {
      setAddingToEmailList(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          MANAGE <span className="text-primary">USERS</span>
        </h2>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchUsers}
            disabled={loading}
            data-testid="refresh-users-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" onClick={onClose} data-testid="close-users-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
        <Input
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-background border-white/10"
          data-testid="search-users-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-background p-4 rounded-sm border border-white/10">
          <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Total Users</p>
          <p className="text-2xl font-bold">{users.length}</p>
        </div>
        <div className="bg-background p-4 rounded-sm border border-white/10">
          <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Admins</p>
          <p className="text-2xl font-bold text-primary">{users.filter(u => u.role === 'admin').length}</p>
        </div>
        <div className="bg-background p-4 rounded-sm border border-white/10">
          <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Regular Users</p>
          <p className="text-2xl font-bold text-success">{users.filter(u => u.role !== 'admin').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-neutral-400">Loading users...</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">
              {searchTerm ? 'No users match your search' : 'No users found'}
            </p>
          ) : (
            filteredUsers.map((user, index) => (
              <div
                key={user.user_id}
                className="flex justify-between items-center p-4 bg-background rounded-sm border border-white/5 hover:border-white/10 transition-colors"
                data-testid={`user-row-${index}`}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    user.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-success/20 text-success'
                  }`}>
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{user.name || 'Unknown'}</p>
                      {user.role === 'admin' && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-primary/20 text-primary flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          ADMIN
                        </span>
                      )}
                      {user.two_fa_enabled && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold bg-success/20 text-success">
                          2FA
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-400 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {user.email}
                    </p>
                    <p className="text-xs text-neutral-500 mt-1">
                      Joined: {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                      {user.auth_type && ` • Auth: ${user.auth_type}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {user.booking_count !== undefined && (
                    <span className="text-sm text-neutral-400">
                      {user.booking_count} booking{user.booking_count !== 1 ? 's' : ''}
                    </span>
                  )}
                  
                  {/* Add to Email List Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-primary/50 text-primary hover:bg-primary hover:text-white"
                    onClick={() => handleAddToEmailList(user.user_id, user.name)}
                    disabled={addingToEmailList === user.user_id}
                    title="Add to saved email list"
                    data-testid={`add-email-btn-${index}`}
                  >
                    {addingToEmailList === user.user_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4" />
                    )}
                  </Button>
                  
                  {user.role !== 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-error text-error hover:bg-error hover:text-white"
                      onClick={() => handleDeleteUser(user.user_id, user.name)}
                      disabled={deletingUserId === user.user_id}
                      data-testid={`delete-user-btn-${index}`}
                    >
                      {deletingUserId === user.user_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <p className="text-xs text-neutral-500 mt-4">
        Note: Admin accounts cannot be deleted. Deleting a user will also remove all their bookings.
        <br />
        <UserPlus className="w-3 h-3 inline mr-1" /> adds users to the saved email list for marketing campaigns.
      </p>
    </motion.div>
  );
};

export default AdminUsers;
